import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/cn";

/**
 * Dialog — modal con backdrop, animaciones y bloqueo de scroll.
 *
 * @example
 * <Dialog open={open} onClose={() => setOpen(false)}>
 *   <DialogHeader>
 *     <DialogTitle>Importar datos</DialogTitle>
 *     <DialogDescription>Sube un CSV mensual de RingOver</DialogDescription>
 *   </DialogHeader>
 *   <DialogContent>...</DialogContent>
 *   <DialogFooter>
 *     <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
 *     <Button onClick={importar}>Importar</Button>
 *   </DialogFooter>
 * </Dialog>
 */

export const Dialog = ({ open, onClose, children, size = "default", className }) => {
  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    default: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-[calc(100vw-2rem)]",
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="fixed inset-0 bg-black/50 animate-in fade-in-0" />
      <div
        className={cn(
          "relative z-50 grid w-full gap-4 bg-background p-0 shadow-lg rounded-lg border",
          "animate-in fade-in-0 zoom-in-95 duration-200",
          "max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col",
          sizeClasses[size],
          className
        )}
        role="dialog"
        aria-modal="true"
      >
        {children}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-md p-1 opacity-70 hover:opacity-100 hover:bg-accent transition-all"
          aria-label="Cerrar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

export const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5 px-6 pt-6 pb-2", className)}
    {...props}
  />
);

export const DialogTitle = ({ className, ...props }) => (
  <h2
    className={cn("text-lg font-medium leading-none tracking-tight", className)}
    {...props}
  />
);

export const DialogDescription = ({ className, ...props }) => (
  <p
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);

export const DialogContent = ({ className, ...props }) => (
  <div
    className={cn("px-6 py-2 overflow-y-auto flex-1", className)}
    {...props}
  />
);

export const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 py-4 border-t bg-muted/30",
      className
    )}
    {...props}
  />
);
